import React, { useEffect, useState } from 'react';
import {
  Pressable, ScrollView, StyleSheet, Text, TextInput, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import SearchIcon from '../components/SearchIcon';
import Colors from '../constants/Colors';
import { RootTabScreenProps } from '../types';
import GroupEditModal from '../components/UserManagement/GroupEditModal';
import getUserGroups from '../requests/Users/GetUserGroups';
import getUsers from '../requests/Users/GetUsers';
import AlertModal from '../components/AlertModal';
import InviteUserModal from '../components/UserManagement/InviteUserModal';
import ScreenEmptyState from '../components/ScreenEmptyState';
import NoUsersImage from '../components/UserManagement/NoUsersImage';
import isMobile from '../components/IsMobile';
import { logEvent } from '../components/LogEvent';
import {
  UserPermission, UserGroup, UserInstance, UserStatus,
} from '../constants/Types';
import {
  ADD_USER_GROUP_SUCCESS, CREATE_ROLE_ERROR, CREATE_ROLE_SUCCESS,
  DELETE_USER_GROUP_SUCCESS, DELETE_USER_SUCCESS,
  EDIT_ROLE_ERROR, EDIT_ROLE_SUCCESS, noPermissionsSubtitle, noPermissionsTitle,
  SEND_INVITE_SUCCESS, SOMETHING_WENT_WRONG,
} from '../constants/AlertMessages';
import { getUserFullName } from '../constants/Utils';
import getRoles from '../requests/Users/GetRoles';
import ProfileIcon from '../components/24pxIcons/ProfileIcon';
import MenuIcon from '../components/16pxIcons/MenuIcon';
import AddUserIcon from '../components/24pxIcons/AddUserIcon';
import MonitorIcon16 from '../components/16pxIcons/MonitorIcon16';
import EllipsisIconVertical from '../components/EllipsisIconVertical';
import EditRoleModal from '../components/UserManagement/EditRoleModal';
import editRole from '../requests/Users/EditRole';
import PlusIcon16 from '../components/16pxIcons/PlusIcon16';
import createRole from '../requests/Users/CreateRole';
import { AppScreen } from '../navigation/LinkingConfiguration';
import Style from '../constants/Style';
import PressableStyled, { PressableState } from '../components/PressableStyled';
import RoleMatrix from '../components/UserManagement/RoleMatrix';
import deleteRole from '../requests/Users/DeleteRole';
import TriangleDown from '../components/TriangleDown';
import DropdownMenu from '../components/DropdownMenu';
import UserRowWebV2 from '../components/UserManagement/UserRowWebV2';
import deleteUserGroup from '../requests/Users/DeleteUserGroup';
import NotificationGroupCard from '../components/UserManagement/NotificationGroupCard';
import getNotificationGroups from '../requests/Users/GetNotificationGroups';

export default function UserMgmtScreenV2({
  // eslint-disable-next-line no-unused-vars
  navigation,
}: RootTabScreenProps<AppScreen.USER_MANAGEMENT>) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_READ);
  const canViewUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_READ);
  const canViewRoles: boolean = isAdmin || permissions.includes(UserPermission.ROLES_READ);
  const canAddUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_CREATE);
  const canAddUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_CREATE);
  const canAddRoles: boolean = isAdmin || permissions.includes(UserPermission.ROLES_CREATE);
  const canUpdateUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_UPDATE);
  const [searchValue, setSearchValue] = useState<string>('');
  const [addGroupsModalVisible, setAddGroupsModalVisible] = useState<boolean>(false);
  const [editRoleModalVisible, setEditRoleModalVisible] = useState<boolean>(false);
  const [inviteModalVisible, setInviteModalVisible] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<UserInstance[]>([]);
  const [groupUsers, setGroupUsers] = useState<UserInstance[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [updateUsers, setUpdateUsers] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | React.ReactElement>('');
  const [availableUsersSelected, setAvailableUsersSelected] = useState<boolean>(true);
  const [roles, setRoles] = useState<object[]>([]);
  const [selectedTab, setSelectedTab] = useState<'users' | 'groups' | 'roles' | null>('groups');
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [selectedRole, setSelectedRole] = useState<any | null>(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [rowsDropdownShown, setRowsDropdownShown] = useState<boolean>(false);
  const [listSize, setListSize] = useState<number>(100);
  const [contextIndexId, setContextIndexId] = useState<string>('');
  const [notificationGroups, setNotificationGroups] = useState(undefined);

  const showAlert = (
    title: string,
    message: string | React.ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
  };

  const loadUserGroups: () => void = () => {
    getUserGroups().then((response) => {
      setLoading(false);
      if (response?.UserGroups?.length > 0) {
        setUserGroups(response.UserGroups);
      }
    });
  };

  useEffect(() => {
    if (!selectedGroup) {
      setGroupUsers([]);
      return;
    }
    getUsers({ userGroupId: selectedGroup?.UserGroupId }).then((response) => {
      if (response?.items?.length > 0) {
        setGroupUsers(response.items);
      } else setGroupUsers([]);
    }).catch(() => setGroupUsers([]));
    getNotificationGroups(selectedGroup.UserGroupId).then((response) => {
      if (response?.Prototypes?.length > 0) {
        setNotificationGroups(response.Prototypes);
      } else setNotificationGroups([]);
    });
  }, [updateUsers, selectedGroup]);

  useEffect(() => {
    loadUserGroups();
    getUsers().then((response) => {
      if (response?.items?.length > 0) {
        setAllUsers(response.items);
      } else setAllUsers([]);
    });
    getRoles().then((response) => {
      // console.log('Roles response:', response);
      if (response?.length > 0) {
        setRoles(response);
      }
    });
  }, [updateUsers]);

  useEffect(loadUserGroups, [loading]);

  const renderAddButtons = () => canAddUsers && <View
    style={{
      flexDirection: 'row',
      marginHorizontal: 10,
    }}
  >
    <PressableStyled
      style={[
        {
          backgroundColor: '#fff',
          flexDirection: 'row',
          borderWidth: 1,
          borderColor: Colors.light.hereworksBlue500,
          borderRadius: 8,
          alignItems: 'center',
          padding: 6,
        },
      ]}
      disabled={selectedTab !== 'users' && !selectedGroup}
      onPress={() => {
        if (selectedGroup) {
          setAddGroupsModalVisible(true);
        } else {
          setInviteModalVisible(true);
          logEvent('UserMgmt', 'Invite_users');
        }
      }}
      testID="invite-users-btn"
    >
      <AddUserIcon />
      <Text style={{
        color: Colors.light.hereworksBlue500,
      }}>{`${selectedTab === 'users' ? 'Invite' : 'Manage'} Users`}</Text>
    </PressableStyled>
  </View>;

  const renderUsers = () => (
    <View style={{ marginHorizontal: 40, marginVertical: 20 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text
          style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 22,
            lineHeight: 33,
            color: Colors.light.hereworksBlack600,
          }}
        >
          {selectedGroup?.Name ? `${selectedGroup.Name}` : 'Users'}
        </Text>
        {!isMobile && renderAddButtons()}
        <View
          style={{
            flexDirection: 'row',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Pressable
            onPress={() => {
              setAvailableUsersSelected(true);
            }}
            style={{
              paddingVertical: 4,
              paddingHorizontal: 12,
              borderRadius: 4,
              backgroundColor: !availableUsersSelected
                ? '#fff'
                : Colors.light.hereworksBlue500,
              borderWidth: 1,
              borderColor: !availableUsersSelected
                ? Colors.light.hereworksBlue300
                : Colors.light.hereworksBlue500,
              marginRight: 8,
            }}
          >
            <Text
              style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                lineHeight: 20,
                color: !availableUsersSelected
                  ? Colors.light.hereworksBlue400
                  : '#fff',
              }}
            >
              AVAILABLE USERS
            </Text>
          </Pressable>
          <Pressable
            onPress={() => setAvailableUsersSelected(false)}
            style={{
              paddingVertical: 4,
              paddingHorizontal: 12,
              borderRadius: 4,
              backgroundColor: availableUsersSelected
                ? '#fff'
                : Colors.light.hereworksBlue500,
              borderWidth: 1,
              borderColor: availableUsersSelected
                ? Colors.light.hereworksBlue300
                : Colors.light.hereworksBlue500,
            }}
          >
            <Text
              style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                lineHeight: 20,
                color: availableUsersSelected
                  ? Colors.light.hereworksBlue400
                  : '#fff',
              }}
            >
              INVITATION SENT
            </Text>
          </Pressable>
        </View>
      </View>
      {selectedGroup?.Description && <Text
        style={{
          fontFamily: 'Roboto_400Regular',
          fontSize: 12,
          lineHeight: 14,
          color: Colors.light.hereworksBlack300,
        }}
      >{selectedGroup?.Description}</Text>}
      <View
        style={{
          flexDirection: 'row',
          paddingVertical: 12,
          alignSelf: 'stretch',
          marginTop: 12,
          borderTopWidth: 1,
          borderColor: Colors.light.hereworksBlue100,
        }}
      >
        <View style={{
          flexDirection: 'row',
          flex: 1,
          marginHorizontal: 24,
          paddingBottom: 10,
          borderBottomWidth: 1,
          borderColor: Colors.light.hereworksWhite500,
          paddingLeft: 20,
        }}>
          <Text style={[styles.headerText, { flex: 1 }]}>
            NAME
          </Text>
          {isAdmin && (
            <Text style={[styles.headerText,
              { flex: 1 }]}>
              ROLE
            </Text>
          )}
          {availableUsersSelected && (
            <Text style={[styles.headerText, { marginLeft: 0, flex: 1 }]}>
              {selectedGroup ? 'EMAIL' : 'GROUP'}
            </Text>)}
          <Text style={[styles.headerText,
            { flex: availableUsersSelected ? 1 : 1 }]}>
            {selectedGroup ? 'LAST ACTIVITY' : 'EMAIL'}
          </Text>
          {availableUsersSelected ? (null) : (
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <Text style={[{ flex: 2 }, styles.headerText]}>STATUS</Text>
              </View>)}
        </View>
      </View>
      {canViewUsers && <View
        style={{
          backgroundColor: '#fff',
          paddingHorizontal: 24,
          borderRadius: 10,
          paddingBottom: 24,
        }}
        testID="users-list-in-user-group"
      >
        {!loading
          && ((selectedGroup ? groupUsers : allUsers).length > 0 ? (
            (selectedGroup ? groupUsers : allUsers)
              ?.filter((user: UserInstance) => !searchValue
                || getUserFullName(user).trim().toLowerCase()
                  .includes(searchValue.trim().toLowerCase()))
              ?.map((user) => {
                if (
                  availableUsersSelected === true
                  && user.status === 'PENDING_VERIFICATION'
                ) {
                  return null;
                }
                if (
                  !availableUsersSelected
                  && user.status !== 'PENDING_VERIFICATION'
                ) {
                  return null;
                }
                return (
                  <UserRowWebV2
                    {...{ user }}
                    userDeleted={() => {
                      showAlert(DELETE_USER_SUCCESS, <Text>
                        <Text style={Style.alertBoldText}>{getUserFullName(user)}</Text>
                        {' has been deleted from the users list'}
                      </Text>, false);
                      setUpdateUsers(!updateUsers);
                    }}
                    showError={(message) => {
                      showAlert(
                        'Error',
                        message ?? SOMETHING_WENT_WRONG,
                        true,
                      );
                    }}
                    roleChanged={() => {
                      setUpdateUsers(!updateUsers);
                    }}
                    roles={roles}
                    userGroups={userGroups}
                    selectedGroupId={selectedGroup?.UserGroupId}
                    groupUsers={groupUsers}
                    contextIndexId={contextIndexId}
                    setContextIndexId={setContextIndexId}
                  />
                );
              })
          ) : (
            <ScreenEmptyState
              image={<NoUsersImage />}
              title={
                availableUsersSelected
                  ? 'No users invited yet'
                  : 'No invitations sent'
              }
              subtitle="Get started by inviting users to join your community"
              buttonTitle="Invite Users"
              actionAvailable
              buttonAction={() => setInviteModalVisible(true)}
            />
          ))}
      </View>}
    </View>
  );

  const renderGroupUsers = () => (
    <View style={{ marginHorizontal: 40, marginVertical: 20 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text
          style={{
            fontFamily: 'Poppins_400Regular',
            fontSize: 22,
            lineHeight: 33,
            color: Colors.light.hereworksBlack600,
          }}
        >
          {selectedGroup?.Name ? `${selectedGroup.Name}` : 'No Group Selected'}
        </Text>
        {!isMobile && renderAddButtons()}
        <View
          style={{
            flexDirection: 'row',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Pressable
            onPress={() => {
              setAvailableUsersSelected(true);
            }}
            style={{
              paddingVertical: 4,
              paddingHorizontal: 12,
              borderRadius: 4,
              backgroundColor: !availableUsersSelected
                ? '#fff'
                : Colors.light.hereworksBlue500,
              borderWidth: 1,
              borderColor: !availableUsersSelected
                ? Colors.light.hereworksBlue300
                : Colors.light.hereworksBlue500,
              marginRight: 8,
            }}
          >
            <Text
              style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                lineHeight: 20,
                color: !availableUsersSelected
                  ? Colors.light.hereworksBlue400
                  : '#fff',
              }}
            >
              USERS
            </Text>
          </Pressable>
          <Pressable
            onPress={() => setAvailableUsersSelected(false)}
            style={{
              paddingVertical: 4,
              paddingHorizontal: 12,
              borderRadius: 4,
              backgroundColor: availableUsersSelected
                ? '#fff'
                : Colors.light.hereworksBlue500,
              borderWidth: 1,
              borderColor: availableUsersSelected
                ? Colors.light.hereworksBlue300
                : Colors.light.hereworksBlue500,
            }}
          >
            <Text
              style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                lineHeight: 20,
                color: availableUsersSelected
                  ? Colors.light.hereworksBlue400
                  : '#fff',
              }}
            >
              NOTIFICATIONS
            </Text>
          </Pressable>
        </View>
      </View>
      {selectedGroup?.Description && <Text
        style={{
          fontFamily: 'Roboto_400Regular',
          fontSize: 12,
          lineHeight: 14,
          color: Colors.light.hereworksBlack300,
        }}
      >{selectedGroup?.Description}</Text>}
      {availableUsersSelected && <View
        style={{
          flexDirection: 'row',
          paddingVertical: 12,
          alignSelf: 'stretch',
          marginTop: 12,
          borderTopWidth: 1,
          borderColor: Colors.light.hereworksBlue100,
        }}
      >
        <View style={{
          flexDirection: 'row',
          flex: 1,
          marginHorizontal: 24,
          paddingBottom: 10,
          borderBottomWidth: 1,
          borderColor: Colors.light.hereworksWhite500,
          paddingLeft: 20,
        }}>
          <Text style={[styles.headerText, { flex: 1 }]}>
            NAME
          </Text>
          {isAdmin && (
            <Text style={[styles.headerText,
              { flex: 1 }]}>
              ROLE
            </Text>
          )}
          {availableUsersSelected && (
            <Text style={[styles.headerText, { marginLeft: 0, flex: 1 }]}>
              {selectedGroup ? 'EMAIL' : 'GROUP'}
            </Text>)}
          <Text style={[styles.headerText,
            { flex: availableUsersSelected ? 1 : 1 }]}>
            {selectedGroup ? 'LAST ACTIVITY' : 'EMAIL'}
          </Text>
          {availableUsersSelected ? (null) : (
            <View style={{ flexDirection: 'row', flex: 1 }}>
              <Text style={[{ flex: 2 }, styles.headerText]}>STATUS</Text>
              </View>)}
        </View>
      </View>}
      {(canViewUsers && availableUsersSelected) && <View
        style={{
          backgroundColor: '#fff',
          paddingHorizontal: 24,
          borderRadius: 10,
          paddingBottom: 24,
        }}
        testID="users-list-in-user-group"
      >
        {!loading
          && (groupUsers.length > 0 ? (
            (selectedGroup ? groupUsers : [])
              ?.filter((user: UserInstance) => !searchValue
                || getUserFullName(user).trim().toLowerCase()
                  .includes(searchValue.trim().toLowerCase()))
              ?.map((user) => {
                if (
                  availableUsersSelected === true
                  && user.status === 'PENDING_VERIFICATION'
                ) {
                  return null;
                }
                if (
                  !availableUsersSelected
                  && user.status !== 'PENDING_VERIFICATION'
                ) {
                  return null;
                }
                return (
                  <UserRowWebV2
                    {...{ user }}
                    userDeleted={() => {
                      showAlert(DELETE_USER_SUCCESS, <Text>
                        <Text style={Style.alertBoldText}>{getUserFullName(user)}</Text>
                        {' has been deleted from the users list'}
                      </Text>, false);
                      setUpdateUsers(!updateUsers);
                    }}
                    showError={(message) => {
                      showAlert(
                        'Error',
                        message ?? SOMETHING_WENT_WRONG,
                        true,
                      );
                    }}
                    roleChanged={() => {
                      setUpdateUsers(!updateUsers);
                    }}
                    roles={roles}
                    userGroups={userGroups}
                    selectedGroupId={selectedGroup?.UserGroupId}
                    groupUsers={groupUsers}
                    contextIndexId={contextIndexId}
                    setContextIndexId={setContextIndexId}
                  />
                );
              })
          ) : (
            <ScreenEmptyState
              image={<NoUsersImage />}
              title={selectedGroup ? 'No members in this group yet' : 'Select a group from the sidebar'}
              subtitle={selectedGroup ? 'Get started by adding members to this user group' : ''}
              buttonTitle="Add Members"
              actionAvailable={!!selectedGroup}
              buttonAction={() => setInviteModalVisible(true)}
            />
          ))}
      </View>}
      {!availableUsersSelected && notificationGroups
          && notificationGroups.length === 0 && !loading && <ScreenEmptyState
            image={<NoUsersImage />}
            title='No notification groups in this group yet'
            subtitle='Get started by adding notification groups to this group'
            buttonTitle='Add Notification Groups'
          />}
        {!availableUsersSelected && notificationGroups && notificationGroups.map(
          (notificationGroup: any, key: number) => (
            <View
              key={key}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <NotificationGroupCard
                title={notificationGroup.Name}
                issues={notificationGroup.Issues}
                groupId={selectedGroup?.UserGroupId} />
            </View>
          ),
        )}
    </View>
  );

  const updatedGroup = (
    groupId: string,
    title: string,
    description: string,
    count: number,
  ) => {
    userGroups.forEach((group: UserGroup) => {
      if (group.UserGroupId === groupId) {
        group.Name = title;
        group.Description = description;
        group.UsersCount = count;
      }
    });
    setAddGroupsModalVisible(false);
    setLoading(true);
    setUpdateUsers(!updateUsers);
    showAlert(ADD_USER_GROUP_SUCCESS, '', false);
  };

  return (canViewRoles || canViewUserGroups || canViewUsers)
    ? <View
      style={{
        paddingVertical: 16,
        backgroundColor: Colors.light.hereworksBackground,
        paddingHorizontal: 40,
        flex: 1,
      }}
    >
      <Text
        style={{
          fontSize: 30,
          lineHeight: 33,
          marginVertical: 28,
          fontFamily: 'Poppins_400Regular',
          color: Colors.light.hereworksBlack600,
        }}
      >
        User Management
      </Text>
      <View style={styles.container}>
        <View
          style={{
            width: 200,
          }}
        >
          {canViewUsers && <Pressable
            style={{
              flexDirection: 'row',
              alignSelf: 'stretch',
              alignItems: 'center',
              marginTop: 24,
              borderBottomWidth: 1,
              backgroundColor:
                selectedTab === 'users'
                  ? Colors.light.hereworksBlue500
                  : Colors.light.hereworksBackground,
              borderColor: Colors.light.hereworksWhite300,
              padding: 12,
              borderRadius: 8,
            }}
            onPress={() => {
              setSelectedTab('users');
              setSelectedGroup(null);
            }}
          >
            <ProfileIcon
              color={
                selectedTab === 'users'
                  ? Colors.light.hereworksWhite500
                  : Colors.light.hereworksBlue500
              }
            />
            <Text
              style={{
                color:
                  selectedTab === 'users'
                    ? Colors.light.hereworksWhite500
                    : Colors.light.hereworksBlue500,
              }}
            >
              Users
            </Text>
          </Pressable>}
          {canViewUserGroups && <View style={{
            marginTop: 24,
            borderWidth: 1,
            borderRadius: 8,
            overflow: 'hidden',
            borderColor: selectedTab === 'groups' ? Colors.light.hereworksBlue500 : Colors.light.hereworksWhite300,
            flex: selectedTab === 'groups' ? 1 : undefined,
          }}>
            <Pressable
              style={{
                flexDirection: 'row',
                alignSelf: 'stretch',
                alignItems: 'center',
                borderBottomWidth: 1,
                backgroundColor:
                  selectedTab === 'groups'
                    ? Colors.light.hereworksBlue500
                    : Colors.light.hereworksBackground,
                borderColor: Colors.light.hereworksWhite300,
                padding: 12,
              }}
              onPress={() => setSelectedTab('groups')}
            >
              <MenuIcon
                fill={
                  selectedTab === 'groups'
                    ? Colors.light.hereworksWhite500
                    : Colors.light.hereworksBlue500
                }
              />
              <Text
                style={{
                  flex: 1,
                  color:
                    selectedTab === 'groups'
                      ? Colors.light.hereworksWhite500
                      : Colors.light.hereworksBlue500,
                }}
              >
                Groups
              </Text>
              <TriangleDown
                color={selectedTab === 'groups' ? Colors.light.hereworksWhite500 : Colors.light.hereworksBlue500}
                style={{
                  transform: [{ rotate: selectedTab === 'groups' ? '180deg' : '0deg' }],
                }} />
            </Pressable>
            {selectedTab === 'groups' ? <ScrollView style={{
            }}>
              {canAddUserGroups && <Pressable
                style={[
                  styles.buttonCommon,
                  {
                    borderColor: '#fff',
                    flex: isMobile ? 1 : null,
                    paddingHorizontal: isMobile ? 0 : 24,
                  },
                ]}
                onPress={() => {
                  setSelectedGroup(null);
                  setAddGroupsModalVisible(!addGroupsModalVisible);
                  logEvent('UserMgmt', 'Add_groups');
                }}
                testID="add-user-group-btn"
              >
                <PlusIcon16 />
                <Text
                  style={{
                    color: Colors.light.hereworksBlue500,
                    fontFamily: 'Roboto_400Regular',
                    fontSize: 16,
                    lineHeight: 24,
                    marginLeft: 8,
                    marginVertical: 8,
                  }}
                  numberOfLines={1}
                >
                  Add group
                </Text>
              </Pressable>}
              {userGroups.map((group: UserGroup) => (
                <Pressable
                  key={group.UserGroupId}
                  onPress={() => {
                    setSelectedGroup(group);
                    setMenuVisible(false);
                    logEvent('UserMgmt', 'select_group');
                  }}
                  style={{
                    padding: 8,
                    marginHorizontal: 8,
                    backgroundColor:
                      selectedGroup?.UserGroupId === group.UserGroupId
                        ? Colors.light.hereworksBlue500
                        : Colors.light.hereworksBackground,
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderRadius: 8,
                    marginTop: 8,
                    zIndex: selectedGroup?.UserGroupId === group.UserGroupId ? 1 : 0,
                  }}
                >
                  <View style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: 8,
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: selectedGroup?.UserGroupId === group.UserGroupId
                      ? '#fff'
                      : Colors.light.hereworksDarkBlue,
                  }}>
                    <MonitorIcon16 color={selectedGroup?.UserGroupId === group.UserGroupId ? Colors.light.hereworksBlue500 : '#fff'} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{
                      color:
                        selectedGroup?.UserGroupId === group.UserGroupId
                          ? Colors.light.hereworksWhite500
                          : Colors.light.hereworksBlack600,
                      fontFamily: selectedGroup?.UserGroupId === group.UserGroupId
                        ? 'Roboto_700Bold'
                        : 'Roboto_400Regular',
                    }}>{group.Name}</Text>
                    <Text style={{
                      fontFamily: 'Roboto_400Regular',
                      fontSize: 10,
                      color: selectedGroup?.UserGroupId === group.UserGroupId
                        ? Colors.light.hereworksWhite500
                        : Colors.light.hereworksBlack400,
                    }}>{group.UsersCount ?? 0} members</Text>
                  </View>
                  {canUpdateUserGroups && <Pressable
                    onPress={() => {
                      setSelectedGroup(group);
                      if (group.UserGroupId === selectedGroup?.UserGroupId) {
                        setMenuVisible(!menuVisible);
                      } else setMenuVisible(true);
                    }}
                    style={({ hovered, pressed }: PressableState) => [
                      hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                      {
                        borderRadius: 8,
                        justifyContent: 'center',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <EllipsisIconVertical color={selectedGroup?.UserGroupId === group.UserGroupId
                      ? '#fff' : Colors.light.hereworksBlack600} />
                  </Pressable>}
                  {menuVisible && selectedGroup?.UserGroupId === group.UserGroupId && (
                    <View style={{
                      position: 'absolute',
                      right: 16,
                      top: 40,
                      backgroundColor: '#fff',
                      borderRadius: 8,
                      padding: 8,
                      shadowColor: '#000',
                      shadowOpacity: 0.2,
                      shadowRadius: 4,
                      elevation: 3,
                      zIndex: 10,
                    }}>
                      <Pressable
                        onPress={() => {
                          setMenuVisible(false);
                          setAddGroupsModalVisible(true);
                        }}
                        style={{ padding: 8 }}
                      >
                        <Text>Edit</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => {
                          setMenuVisible(false);
                          logEvent('GroupEditModal', 'delete_group');
                          showAlert(
                            'Delete user group',
                            <>
                              {'Are you sure you want to delete the user group '}
                              <Text style={{ fontFamily: 'Roboto_700Bold' }}>
                                {`${selectedGroup?.Name ?? ''}?`}
                              </Text>
                            </>,
                            true,
                            () => {
                              setLoading(true);
                              deleteUserGroup(selectedGroup?.UserGroupId).then((response) => {
                                if (response && !response.isError) {
                                  setLoading(true);
                                  setSelectedGroup(null);
                                  showAlert(
                                    DELETE_USER_GROUP_SUCCESS,
                                    <>
                                      <Text style={{ fontFamily: 'Roboto_700Bold' }}>
                                        {`${selectedGroup?.Name ?? ''}?`}
                                      </Text>
                                      {' has been deleted from groups list'}
                                    </>,
                                    false,
                                    null,
                                    '',
                                  );
                                } else showAlert('Error', response?.errorText, true);
                              }).catch((error) => showAlert('Error', error.message, true));
                            },
                            'Delete',
                          );
                        }}
                        style={{ padding: 8 }}
                      >
                        <Text>Delete</Text>
                      </Pressable>
                    </View>
                  )}
                </Pressable>
              ))}
            </ScrollView> : null}
          </View>}
          {canViewRoles && <View style={{
            marginTop: 24,
            borderWidth: 1,
            borderRadius: 8,
            overflow: 'hidden',
            borderColor: selectedTab === 'roles' ? Colors.light.hereworksBlue500 : Colors.light.hereworksWhite300,
            flex: selectedTab === 'roles' ? 1 : undefined,
          }}>
            <Pressable
              style={{
                flexDirection: 'row',
                alignSelf: 'stretch',
                alignItems: 'center',
                borderBottomWidth: 1,
                backgroundColor:
                  selectedTab === 'roles'
                    ? Colors.light.hereworksBlue500
                    : Colors.light.hereworksBackground,
                borderColor: Colors.light.hereworksWhite300,
                padding: 12,
              }}
              onPress={() => {
                setSelectedTab('roles');
                setSelectedGroup(null);
              }}
            >
              <ProfileIcon
                color={
                  selectedTab === 'roles'
                    ? Colors.light.hereworksWhite500
                    : Colors.light.hereworksBlue500
                }
              />
              <Text
                style={{
                  flex: 1,
                  color:
                    selectedTab === 'roles'
                      ? Colors.light.hereworksWhite500
                      : Colors.light.hereworksBlue500,
                }}
              >
                Roles
              </Text>
              <TriangleDown
                color={selectedTab === 'roles' ? Colors.light.hereworksWhite500 : Colors.light.hereworksBlue500}
                style={{
                  transform: [{ rotate: selectedTab === 'roles' ? '180deg' : '0deg' }],
                }}
              />
            </Pressable>
            {selectedTab === 'roles' ? <ScrollView style={{
            }}>
              {roles.map((role: any) => (
                <Pressable
                  key={role.UserGroupId}
                  onPress={() => {
                    setSelectedRole(role);
                    logEvent('UserMgmt', 'select_group');
                  }}
                  style={{
                    padding: 8,
                    marginHorizontal: 8,
                    backgroundColor:
                      selectedRole?.id === role.id
                        ? Colors.light.hereworksBlue500
                        : Colors.light.hereworksBackground,
                    borderRadius: 8,
                    marginTop: 8,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: selectedRole?.id === role.id ? 1 : 0,
                  }}
                >
                  <View>
                    <Text style={{
                      color:
                        selectedRole?.id === role.id
                          ? Colors.light.hereworksWhite500
                          : Colors.light.hereworksBlack600,
                      fontFamily: selectedRole?.id === role.id
                        ? 'Roboto_700Bold'
                        : 'Roboto_400Regular',
                      fontSize: 12,
                    }}>{role?.name ?? 'Role'}</Text>
                    <Text style={{
                      color:
                        selectedRole?.id === role.id
                          ? Colors.light.hereworksWhite500
                          : Colors.light.hereworksBlack400,
                      fontFamily: 'Roboto_400Regular',
                      fontSize: 10,
                      marginTop: 2,
                      flex: 1,
                    }}>{role?.assignedUsersCount ?? '0'} members</Text>
                  </View>
                  {isAdmin ? (
                    <Pressable
                      onPress={() => {
                        setSelectedRole(role);
                        if (role.id === selectedRole?.id) {
                          setMenuVisible(!menuVisible);
                        } else setMenuVisible(true);
                      }}
                      style={() => [{
                        borderRadius: 8,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }]}
                    >
                      <EllipsisIconVertical color={selectedRole?.id === role.id
                        ? '#fff' : Colors.light.hereworksBlack600} />
                    </Pressable>
                  ) : null}
                  {menuVisible && selectedRole?.id === role.id && (
                    <View style={{
                      position: 'absolute',
                      right: 16,
                      top: 40,
                      backgroundColor: '#fff',
                      borderRadius: 8,
                      padding: 8,
                      shadowColor: '#000',
                      shadowOpacity: 0.2,
                      shadowRadius: 4,
                      elevation: 3,
                      zIndex: 10,
                    }}>
                      <Pressable
                        onPress={() => {
                          setMenuVisible(false);
                          setEditRoleModalVisible(true);
                        }}
                        style={{ padding: 8 }}
                      >
                        <Text>Edit</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => {
                          setMenuVisible(false);
                          if (role.assignedUsersCount > 0) {
                            showAlert(
                              'Cannot delete role',
                              'This role is assigned to one or more users and cannot be deleted.',
                              true,
                              () => { },
                            );
                            return;
                          }
                          showAlert(
                            'Delete role',
                            <>
                              {'Are you sure you want to delete the role '}
                              <Text style={Style.alertBoldText}>{role.name}</Text>
                            </>,
                            true,
                            () => deleteRole(role.id).then(() => {
                              setSelectedRole(null);
                              setUpdateUsers(!updateUsers);
                              showAlert(
                                'Role deleted',
                                'The role has been successfully deleted.',
                                false,
                                () => { },
                              );
                            }).catch((error) => {
                              showAlert(
                                'Error',
                                error.message || SOMETHING_WENT_WRONG,
                                true,
                                () => { },
                              );
                            }),
                            'Delete',
                          );
                        }}
                        style={{ padding: 8 }}
                      >
                        <Text>Delete</Text>
                      </Pressable>
                    </View>
                  )}
                </Pressable>
              ))}
            </ScrollView> : null}
          </View>}
        </View>
        <View style={{
          flex: 1,
        }}>
          <ScrollView contentContainerStyle={{
            paddingBottom: 40,
          }}>
            {selectedTab === 'users' && (
              <View
                style={{
                  alignSelf: 'stretch',
                }}
              >
                {renderUsers()}
              </View>
            )}
            {selectedTab === 'groups' && (
              <View
                style={{
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    zIndex: 4,
                  }}
                >
                  <View
                    style={[
                      styles.dropdownBox,
                      { flex: 1, marginHorizontal: isMobile ? 24 : 40 },
                    ]}
                  >
                    <TextInput
                      style={[
                        styles.dropdownBoxText,
                        { outlineWidth: 0, marginLeft: 18 },
                      ]}
                      placeholder={'Search'}
                      testID="um-search-bar-input-field"
                      onChangeText={(text) => {
                        setSearchValue(text);
                      }}
                    />
                    <View style={{ alignItems: 'flex-end', marginRight: 15 }}>
                      <SearchIcon />
                    </View>
                  </View>
                </View>
                {renderGroupUsers()}
              </View>
            )}
            {selectedTab === 'roles' && (
              <View
                style={{
                  alignSelf: 'stretch',
                }}
              >
                <View style={{
                  flex: 1, backgroundColor: 'white', borderRadius: 10, padding: 20,
                }}>
                  <View style={{ flexDirection: 'row', marginBottom: 20 }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{
                        fontSize: 18,
                        fontWeight: 'bold',
                        marginBottom: 6,
                        marginTop: 30,
                      }}>{selectedRole?.name}</Text>
                      <Text style={{
                        fontSize: 12,
                        color: Colors.light.hereworksBlack400,
                        flex: 1,
                      }}>{selectedRole?.description ?? 'No description'}</Text>
                    </View>
                    {canAddRoles && <Pressable
                      onPress={() => {
                        setSelectedRole(null);
                        setEditRoleModalVisible(true);
                      }}
                      style={{
                        marginTop: 20,
                        padding: 10,
                        backgroundColor: Colors.light.hereworksBlue500,
                        borderRadius: 8,
                        flexDirection: 'row',
                        alignSelf: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <PlusIcon16 color='#fff' />
                      <Text style={{
                        marginLeft: 8,
                        color: '#fff',
                        fontFamily: 'Roboto_500Medium',
                      }}>
                        {'Add new role'}
                      </Text>
                    </Pressable>}
                  </View>
                  <RoleMatrix selectedRole={selectedRole} />
                </View>
              </View>
            )}
          </ScrollView>
          {selectedTab !== 'roles' && <View style={{ flexDirection: 'row', alignItems: 'center', margin: 10 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
              <Text style={{ }}>{'Rows per page:'}</Text>
              <DropdownMenu
                expanded={rowsDropdownShown}
                setExpanded={setRowsDropdownShown}
                options={[
                  100,
                  200,
                  500,
                ].map((size: number) => ({
                  value: size,
                  name: size.toString(),
                }))}
                selectedOption={({ value: listSize, name: listSize.toString() })}
                setSelectedOption={(option: { value: number }) => setListSize(option.value)}
                style={styles.listSizeDropdown}
                hideSelectAll
                top
              />
            </View>
          {(allUsers?.length > 0 && !!allUsers) && <Text style={{}}>
                {`${groupUsers.length ? 1 : 0}-${Math.min(allUsers.length, groupUsers.length)} of ${groupUsers.length} showing`}
              </Text>}
          </View>}
        </View>
        <AlertModal
          modalVisible={alertVisible}
          error={alertError}
          title={alertTitle}
          message={alertMessage}
          setModalVisible={setAlertVisible}
          action={alertAction}
          actionTitle={alertActionTitle}
        />
        <GroupEditModal
          modalVisible={addGroupsModalVisible}
          setModalVisible={setAddGroupsModalVisible}
          updatedGroup={updatedGroup}
          users={allUsers}
          title={selectedGroup?.Name || undefined}
          description={selectedGroup?.Description || ''}
          groupId={selectedGroup?.UserGroupId || ''}
          groupDeleted={() => {
            setAddGroupsModalVisible(false);
          }}
          notificationGroups={[]}
        />
        <InviteUserModal
          modalVisible={inviteModalVisible}
          setModalVisible={setInviteModalVisible}
          users={allUsers.filter(
            (user: UserInstance) => user.status === UserStatus.VERIFIED,
          )}
          invitedUser={(name) => {
            setInviteModalVisible(false);
            setUpdateUsers(!updateUsers);
            showAlert(SEND_INVITE_SUCCESS, <Text>{'User '}<Text style={Style.alertBoldText}>{name}</Text>{' has received your invitation.'}</Text>, false);
          }}
          roles={roles}
        />
        <EditRoleModal
          roleName={selectedRole?.name || ''}
          isVisible={editRoleModalVisible}
          role={selectedRole}
          onClose={() => {
            setSelectedRole(null);
            setEditRoleModalVisible(false);
          }}
          onSave={(name, roleDescription, rolePermissions, roleColor) => {
            if (selectedRole?.id) {
              editRole(name || selectedRole?.name || '', roleDescription || selectedRole?.description || '', rolePermissions, selectedRole?.id || '', roleColor)
                .then(() => {
                  setEditRoleModalVisible(false);
                  setUpdateUsers(!updateUsers);
                  showAlert(EDIT_ROLE_SUCCESS, '', false);
                }).catch((error) => {
                  showAlert(EDIT_ROLE_ERROR, error.message, true);
                });
            } else {
              createRole(name || '', roleDescription || '', rolePermissions, roleColor).then((response) => {
                if (response?.isError) {
                  showAlert(CREATE_ROLE_ERROR, response.errorText || SOMETHING_WENT_WRONG, true);
                  return;
                }
                setEditRoleModalVisible(false);
                setUpdateUsers(!updateUsers);
                showAlert(CREATE_ROLE_SUCCESS, '', false);
              }).catch((error) => {
                showAlert(CREATE_ROLE_ERROR, error.message, true);
              });
            }
          }}
        />
      </View>
    </View> : <ScreenEmptyState
      title={noPermissionsTitle('users, user groups or roles')}
      subtitle={noPermissionsSubtitle('users, user groups or roles')}
      image={<NoUsersImage />}
      large
    />;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 10,
    borderWidth: 1,
    paddingStart: 24,
    paddingBottom: 24,
    borderColor: Colors.light.hereworksWhite300,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'black',
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    marginTop: 24,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  buttonCommon: {
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 15,
    letterSpacing: 0.7,
    color: Colors.light.hereworksBlack400,
  },
  listSizeDropdown: {
    marginStart: 8,
    width: 74,
  },
});
